/**
 * The shared fetch wrapper behind every authenticated client.
 *
 * Authentication rides on an HttpOnly session cookie the page cannot read. Every
 * write must also echo a CSRF token from a second, readable cookie — that double
 * submit is what stops another site driving these endpoints. The two account
 * domains use different cookie names, so each client passes its own.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the session has gone — the UI should return to a sign-in form. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True when the server refused because the name is taken, and similar. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** True when the caller is being throttled. */
  get isThrottled(): boolean {
    return this.status === 429;
  }
}

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** FastAPI reports validation problems as a list; turn that into one sentence. */
export function describeError(status: number, body: unknown): string {
  if (typeof body === "object" && body !== null && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      const parts = detail
        .map((item) => {
          if (typeof item !== "object" || item === null) return null;
          const entry = item as { loc?: unknown[]; msg?: string };
          const field = (entry.loc ?? [])
            .filter((piece) => piece !== "body" && typeof piece !== "number")
            .join(" › ");
          return field ? `${field}: ${entry.msg}` : entry.msg;
        })
        .filter(Boolean);
      if (parts.length > 0) return parts.join("; ");
    }
  }
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status >= 500)
    return "The server could not complete that. Try again shortly.";
  return `Request failed (${status}).`;
}

export interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

/**
 * Perform one request against `${prefix}${path}`.
 *
 * `csrfCookie` names the cookie whose value is echoed back on writes; pass null
 * for an endpoint set that never mutates.
 */
export async function requestJson<T>(
  prefix: string,
  path: string,
  { method = "GET", body, signal }: RequestOptions = {},
  csrfCookie: string | null = null,
): Promise<T> {
  // FormData carries its own multipart boundary, so the browser must set the
  // content type. Handled here rather than by a second copy of this function,
  // which is what the avatar upload used to be — and it had already drifted,
  // reporting a proxy's HTML error page as unreadable where every other call
  // reported the status.
  const isForm = body instanceof FormData;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined && !isForm)
    headers["Content-Type"] = "application/json";

  if (method !== "GET" && csrfCookie) {
    const token = readCookie(csrfCookie);
    if (token) headers["X-CSRF-Token"] = token;
  }

  const response = await fetch(`${prefix}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    signal,
    body:
      body === undefined
        ? null
        : isForm
          ? (body as FormData)
          : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();

  // A reverse proxy answers a gateway timeout with HTML, not JSON. Parsing that
  // must not throw past the caller as a bare SyntaxError, or the reader sees
  // "Unexpected token '<'" instead of something they can act on.
  let parsed: unknown = null;
  let parseFailed = false;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parseFailed = true;
    }
  }

  if (!response.ok) {
    throw new ApiError(describeError(response.status, parsed), response.status);
  }
  if (parseFailed) {
    throw new ApiError(
      "The server sent a response we could not read.",
      response.status,
    );
  }
  return parsed as T;
}
